/**
 * Zustand store for SmartWiz Lite Home navigation state.
 * Manages navigation between onboarding steps, settings pages, and settings grid.
 */
import { create } from "zustand";
import type { OnboardingStepId, SettingsPageId } from "../components/types";

export type LiteHomeView =
  | { type: "onboarding-grid" }
  | { type: "settings-grid" }
  | { type: "onboarding-step"; stepId: OnboardingStepId }
  | { type: "settings-page"; pageId: SettingsPageId };

interface LiteHomeState {
  // Current view state
  currentView: LiteHomeView;

  // Navigation actions
  showOnboardingGrid: () => void;
  showSettingsGrid: () => void;
  showOnboardingStep: (stepId: OnboardingStepId) => void;
  showSettingsPage: (pageId: SettingsPageId) => void;
  goBack: () => void;

  // URL sync
  syncFromUrl: (step: string | null, setting: string | null) => void;
  getUrlParams: () => { step?: string; setting?: string };

  // History for back navigation
  previousView: LiteHomeView | null;
}

export const useLiteHomeStore = create<LiteHomeState>()((set, get) => ({
  // Initial state - show onboarding grid by default
  currentView: { type: "onboarding-grid" },
  previousView: null,

  showOnboardingGrid: () => {
    const { currentView } = get();
    set({
      previousView: currentView,
      currentView: { type: "onboarding-grid" },
    });
  },

  showSettingsGrid: () => {
    const { currentView } = get();
    set({
      previousView: currentView,
      currentView: { type: "settings-grid" },
    });
  },

  showOnboardingStep: (stepId: OnboardingStepId) => {
    const { currentView } = get();
    set({
      previousView: currentView,
      currentView: { type: "onboarding-step", stepId },
    });
  },

  showSettingsPage: (pageId: SettingsPageId) => {
    const { currentView } = get();
    set({
      previousView: currentView,
      currentView: { type: "settings-page", pageId },
    });
  },

  goBack: () => {
    const { previousView, currentView } = get();

    // Determine smart back navigation based on current view
    if (currentView.type === "settings-page") {
      // From settings page -> go to settings grid
      set({
        previousView: currentView,
        currentView: { type: "settings-grid" },
      });
    } else if (currentView.type === "onboarding-step") {
      // From onboarding step -> go to onboarding grid
      set({
        previousView: currentView,
        currentView: { type: "onboarding-grid" },
      });
    } else if (previousView) {
      // Otherwise use previous view
      set({
        currentView: previousView,
        previousView: null,
      });
    } else {
      // Default fallback to onboarding grid
      set({
        previousView: currentView,
        currentView: { type: "onboarding-grid" },
      });
    }
  },

  syncFromUrl: (step: string | null, setting: string | null) => {
    if (setting) {
      const upperSettingId = setting.toUpperCase() as SettingsPageId;
      set({
        currentView: { type: "settings-page", pageId: upperSettingId },
      });
    } else if (step) {
      const upperStepId = step.toUpperCase() as OnboardingStepId;
      set({
        currentView: { type: "onboarding-step", stepId: upperStepId },
      });
    } else {
      // No query params - show default view
      const { currentView } = get();
      // If we're already on a settings or onboarding page, maintain that context
      if (currentView.type === "settings-page") {
        set({ currentView: { type: "settings-grid" } });
      } else {
        set({ currentView: { type: "onboarding-grid" } });
      }
    }
  },

  getUrlParams: () => {
    const { currentView } = get();

    if (currentView.type === "onboarding-step") {
      return { step: currentView.stepId.toLowerCase() };
    } else if (currentView.type === "settings-page") {
      return { setting: currentView.pageId.toLowerCase() };
    }

    return {};
  },
}));
